import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { PaywallBoundaryFallback } from '../PaywallBoundary/PaywallBoundaryFallback';
import { usePaywallPage } from '../PaywallProvider';

import type { PaywallRouteGateProps } from './PaywallRouteGate.types';
import type React from 'react';

/**
 * PaywallRouteGate — the routed-view slot of the web presenter (feature-gate
 * PRD, D2/D3). `PrivateLayout` wraps its `<Outlet />` in this gate; when the
 * provider presents a page-level refusal, the gate renders the server-driven
 * `PaywallPrompt` INSTEAD of the routed view — the page unmounts, so a surface
 * that was refused stops running entirely.
 *
 * Route-scoped by rule: any navigation clears the presentation, so a paywall
 * never outlives the page that earned it.
 *
 * Passthrough outside a `PaywallProvider` (`usePaywallPage` is non-throwing),
 * so layouts rendered bare in stories and tests are unaffected.
 */
export function PaywallRouteGate({
  children,
}: PaywallRouteGateProps): React.JSX.Element {
  const page = usePaywallPage();
  const location = useLocation();
  const clearPagePaywall = page?.clearPagePaywall;

  useEffect(() => {
    // Runs on mount (a no-op — nothing is presented yet) and again on every
    // navigation, which is exactly the route scope D3 asks for.
    clearPagePaywall?.();
  }, [location.key, clearPagePaywall]);

  if (!page?.pageError) {
    return <>{children}</>;
  }

  return (
    <div
      className="mx-auto w-full max-w-2xl py-8 sm:py-12"
      data-testid="paywall-route-gate"
    >
      <PaywallBoundaryFallback error={page.pageError} />
    </div>
  );
}

export default PaywallRouteGate;
